import axios from 'axios';
import downloadPrivatePhoto from './privatePhotoDownload';

export const WATERMARKED_PHOTO_ROUTE = '/photos/:nativePhotoId/watermarked/:version';
export const ORIGINAL_PHOTO_ROUTE = '/photos/:nativePhotoId/original';
const VERSION = /^[a-f0-9]{64}$/;
const positiveId = value => ['string', 'number'].includes(typeof value) && /^[1-9][0-9]*$/.test(String(value)) && Number.isSafeInteger(Number(value));
const codes = new Set(['PHOTO_WATERMARK_PENDING', 'PHOTO_WATERMARK_INCOMPLETE', 'PHOTO_WATERMARK_STALE', 'PHOTO_WATERMARK_FAILED']);
const unavailable = code => Object.assign(new Error('PRIVATE_PHOTO_UNAVAILABLE'), { code: codes.has(code) ? code : 'PRIVATE_PHOTO_UNAVAILABLE' });
const nativeApi = axios.create({ baseURL: process.env.REACT_APP_API_URL });

export function isWatermarkedPhotoPath(path) {
  const match = typeof path === 'string' && /^\/photos\/([1-9][0-9]*)\/watermarked\/([a-f0-9]{64})\/?$/.exec(path);
  return !!(match && positiveId(match[1]));
}
export function watermarkedPhotoEndpoint(photoId, version) {
  if (!positiveId(photoId) || typeof version !== 'string' || !VERSION.test(version)) throw unavailable();
  return `/collector_values/photos/${photoId}/watermarked/${version}/url`;
}
export function isOriginalPhotoPath(path) {
  const match = typeof path === 'string' && /^\/photos\/([1-9][0-9]*)\/original\/?$/.exec(path);
  return !!(match && positiveId(match[1]));
}
export function originalPhotoEndpoint(photoId) {
  if (!positiveId(photoId)) throw unavailable();
  return `/collector_values/photos/${photoId}/original/url`;
}
export function photoOwner(profile) {
  const owner = { organizationId: profile?.organization?.id, companyId: profile?.company?.id,
    entityId: profile?.entity?.id, userId: profile?.user?.id, profileId: profile?.user?.profileId };
  return Object.values(owner).every(value => Number.isSafeInteger(value) && value > 0) ? owner : null;
}
export function photoOwnerKey(owner) {
  if (!owner || !['organizationId', 'companyId', 'entityId', 'userId', 'profileId'].every(key => Number.isSafeInteger(owner[key]) && owner[key] > 0)) return null;
  // Same persisted-profile identity used by the existing private report viewer.
  return JSON.stringify([owner.userId, owner.profileId, owner.entityId, owner.organizationId, owner.companyId]);
}
export function persistedPhotoOwnerKey(storage = localStorage) {
  try { return photoOwnerKey(photoOwner(JSON.parse(storage.getItem('synapse_profile')))); }
  catch (_) { return null; }
}

// A request owns one revocable Blob URL. Signed capabilities remain inside transport memory.
export function loadWatermarkedPhoto({ photoId, version, owner, isCurrent = () => true,
  api = nativeApi, urls = URL, storage = localStorage, Controller = AbortController,
  fetcher, timeoutMs = 125000, monotonicNow, original = false } = {}) {
  const endpoint = original ? originalPhotoEndpoint(photoId) : watermarkedPhotoEndpoint(photoId, version);
  const ownerKey = photoOwnerKey(owner);
  if (!ownerKey) throw unavailable();
  const token = storage.getItem('synapse_token');
  if (!token) throw unavailable();
  const controller = new Controller(), ticketRequest = axios.CancelToken.source();
  let cancelled = false, objectUrl = null, nativeCode, contentType;
  const cancel = () => {
    cancelled = true; clearTimeout(timer); controller.abort(); ticketRequest.cancel('PHOTO_DOWNLOAD_CANCELLED');
    if (objectUrl) urls.revokeObjectURL(objectUrl);
    objectUrl = null;
  };
  const current = () => {
    let valid = false;
    try { valid = !cancelled && !controller.signal.aborted && storage.getItem('synapse_token') === token &&
      persistedPhotoOwnerKey(storage) === ownerKey && isCurrent(); } catch (_) { /* Fail closed if session storage becomes unavailable. */ }
    if (!valid) cancel();
    return valid;
  };
  const timer = setTimeout(cancel, timeoutMs);
  const done = (async () => {
    try {
      const blob = await downloadPrivatePhoto({ path: endpoint.slice(0, -4), signal: controller.signal,
        watermark: original ? undefined : { photoId, version, owner }, isCurrent: current, fetcher, monotonicNow,
        requestTicket: async () => {
          try {
            const response = await api({ url: endpoint, method: 'get', responseType: 'json', timeout: 10000,
              cancelToken: ticketRequest.token, headers: { Authorization: `Bearer ${token}` } });
            return response.data;
          } catch (error) { nativeCode = error?.response?.data?.code; throw unavailable(); }
        } });
      if (!current()) throw unavailable();
      contentType = blob.type;
      objectUrl = urls.createObjectURL(blob);
      return objectUrl;
    } catch (_) { cancel(); throw unavailable(nativeCode); }
    finally { clearTimeout(timer); }
  })();
  return { done, cancel, isCurrent: current, get contentType() { return contentType; } };
}
export function loadOriginalPhoto(options) {
  return loadWatermarkedPhoto({ ...options, original: true });
}
